import React from 'react';
import Reveal from './Reveal';

const CHECKS = [
  'Joint limits',
  'Motion continuity',
  'Target at TCP',
  'Configuration stability',
  'IO-wait bounds',
  'Timing',
  'Gripper-body collision',
  'Part-in-fingers collision',
];

const VerificationSection = () => (
  <section id="verification" className="py-28 bg-white dark:bg-black relative overflow-hidden">
    <div
      className="absolute inset-0 bg-grid"
      style={{ maskImage: 'radial-gradient(ellipse 70% 60% at 50% 40%, black 30%, transparent 100%)', WebkitMaskImage: 'radial-gradient(ellipse 70% 60% at 50% 40%, black 30%, transparent 100%)' }}
    ></div>
    <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
      <Reveal>
        <div className="font-mono text-xs tracking-[0.25em] uppercase text-emerald-600 dark:text-emerald-400 mb-4">04 — Verification</div>
        <h2 className="text-4xl sm:text-5xl md:text-6xl font-bold tracking-tight text-gray-900 dark:text-white mb-6 max-w-3xl">
          What you watch is the program
        </h2>
        <p className="text-lg sm:text-xl text-gray-600 dark:text-gray-400 max-w-2xl mb-14">
          Every program runs an 8-check kinematic oracle — 600+ collision samples
          per program, gripper body and part-in-fingers included. Out-of-reach,
          over-payload, or blocked-transit gets a structured refusal with the
          number. Never a hallucinated success.
        </p>
      </Reveal>

      <div className="grid grid-cols-2 md:grid-cols-4 gap-px bg-black/10 dark:bg-white/10 border border-black/10 dark:border-white/10 mb-14">
        {CHECKS.map((c, i) => (
          <Reveal key={c} delay={i * 50} className="bg-white dark:bg-black">
            <div className="p-5 flex items-center gap-3 hover:bg-black/[0.03] dark:hover:bg-white/[0.03] transition-colors duration-300">
              <svg width="16" height="16" viewBox="0 0 24 24" className="flex-shrink-0">
                <path d="M4 12.5 L10 18 L20 6" stroke="#34d399" strokeWidth="3" fill="none" strokeLinecap="round" />
              </svg>
              <span className="font-mono text-xs sm:text-sm text-gray-700 dark:text-gray-300">{c}</span>
            </div>
          </Reveal>
        ))}
      </div>

      <Reveal>
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-20 items-center">
          <div className="rounded-xl overflow-hidden border border-black/10 dark:border-white/10 shadow-2xl shadow-black/20 dark:shadow-black/60 bg-gray-950">
            <img
              src="/images/product/verified.webp"
              alt="Completed assembly with the full verification summary"
              loading="lazy"
              className="w-full block"
            />
          </div>
          <div>
            <p className="text-lg text-gray-700 dark:text-gray-300 leading-relaxed mb-6">
              The agent can get it wrong — that is exactly why every program
              must pass all 8 checks before it is marked verified. What passes
              runs{' '}
              <span className="text-emerald-600 dark:text-emerald-400 font-semibold">unmodified on the real robot controller software</span>,
              and every run is timed, so cycle-time predictions keep getting
              more accurate.
            </p>
            <p className="text-lg text-gray-600 dark:text-gray-400 leading-relaxed">
              The 3D playback is driven by the same physics the checks ran
              against — and your engineers still review and approve every
              program. Verification means mistakes get caught before they reach
              your floor, not that mistakes never happen.
            </p>
          </div>
        </div>
      </Reveal>
    </div>
  </section>
);

export default VerificationSection;
